import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Settings } from "lucide-react";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { NotifySettings } from "./NotifySettings";

const useNotifications = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const onMsg = (e) => { if (e.data?.type === "vavlis:notification") load(); };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    window.addEventListener("vavlis:notify", load);
    return () => { clearInterval(id); navigator.serviceWorker?.removeEventListener("message", onMsg); window.removeEventListener("vavlis:notify", load); };
  }, [load]);
  return [data, load];
};

const Row = ({ n, onOpen }) => (
  <li>
    <button type="button" onClick={() => onOpen(n)} data-testid={`notification-${n.id}`}
      className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-sky-50/60"}`}>
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-[#0080FF]"}`} />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-navy">{n.title}</span>
        <span className="block truncate text-xs text-slate-600">{n.body}</span>
        <span className="block text-[11px] text-slate-400">{fmtDateTime(n.created_at)}</span>
      </span>
    </button>
  </li>
);

export const NotificationBell = ({ dark }) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const [{ items, unread }, load] = useNotifications();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const openOne = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (n.url && n.url !== "/") nav(n.url);
  };
  const readAll = () => api.post("/notifications/read-all").then(load);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="notification-bell" aria-label={t("x.push.bell")} className={`relative grid h-11 w-11 place-items-center rounded-full ${dark ? "hover:bg-white/10" : "hover:bg-black/10"}`}>
            <Bell className="h-5 w-5" />
            {unread > 0 && <span data-testid="notification-unread-count" className="absolute right-0.5 top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy ring-2 ring-navy">{unread > 99 ? "99+" : unread}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={6} collisionPadding={12} className="w-[min(calc(100vw-24px),380px)] p-0" data-testid="notification-panel">
          <div className="flex items-center gap-1 border-b border-slate-100 px-4 py-2">
            <p className="mr-auto text-sm font-semibold text-navy">{t("x.push.bell")}</p>
            <button type="button" onClick={readAll} className="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label={t("x.push.readAll")} title={t("x.push.readAll")} data-testid="notification-read-all"><CheckCheck className="h-4 w-4" /></button>
            <button type="button" onClick={() => { setOpen(false); setSettings(true); }} className="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label={t("x.push.settings")} data-testid="notification-settings-button"><Settings className="h-4 w-4" /></button>
          </div>
          {items.length ? <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">{items.map((n) => <Row key={n.id} n={n} onOpen={openOne} />)}</ul>
            : <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">{t("x.push.empty")}</p>}
        </PopoverContent>
      </Popover>
      <Dialog open={settings} onOpenChange={setSettings}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto p-0" data-testid="notification-settings-dialog">
          <DialogTitle className="sr-only">{t("x.push.settings")}</DialogTitle>
          {settings && <NotifySettings onTest={load} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
